import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ArticleCard from "../components/ArticleCard";
import { ArrowLeft, User, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const AuthorPage = () => {
  const { authorId } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuthor();
    fetchArticles();
    window.scrollTo(0, 0);
  }, [authorId]);

  const fetchAuthor = async () => {
    try {
      const response = await axios.get(`${API}/authors/${authorId}`);
      setAuthor(response.data);
    } catch (error) {
      console.error("Failed to fetch author:", error);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?author=${authorId}`);
      setArticles(response.data.articles);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!author) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-16">
        <h1 className="font-heading font-black text-4xl uppercase tracking-tighter">Author Not Found</h1>
        <Link to="/authors" className="mt-4">
          <Button variant="outline" className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-6 font-mono uppercase tracking-widest text-xs">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Authors
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen" data-testid="author-page">
      {/* Author Header */}
      <section className="py-12 md:py-16 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Link to="/authors" className="inline-block mb-8">
            <Button 
              variant="outline"
              className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-4 font-mono uppercase tracking-widest text-xs"
              data-testid="author-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              All Writers
            </Button>
          </Link>

          <div className="flex flex-col md:flex-row gap-8 items-start">
            {author.avatar ? (
              <img
                src={author.avatar}
                alt={author.name}
                className="w-32 h-32 md:w-48 md:h-48 rounded-full object-cover"
                data-testid="author-avatar"
              />
            ) : (
              <div className="w-32 h-32 md:w-48 md:h-48 rounded-full bg-secondary flex items-center justify-center">
                <User className="h-16 w-16 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1">
              <span className="font-mono text-xs uppercase tracking-widest text-primary">
                {author.role}
              </span>
              <h1 
                className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter mt-2"
                data-testid="author-name"
              >
                {author.name}
              </h1>
              <p className="text-muted-foreground mt-4 text-lg max-w-2xl" data-testid="author-bio">
                {author.bio}
              </p>
              
              {/* Social Links */}
              {author.social_links && Object.keys(author.social_links).length > 0 && (
                <div className="flex flex-wrap gap-4 mt-6">
                  {Object.entries(author.social_links).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                      data-testid={`author-social-${platform}`}
                    >
                      {platform}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Author's Articles */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mb-12">
            Articles by {author.name} ({articles.length})
          </h2>
          
          {articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No articles published yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {articles.map((article) => (
                <ArticleCard key={article.article_id} article={article} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AuthorPage;
